import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, thumbUrl, brandAssetUrl } from "@/lib/api";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import { Search } from "lucide-react";

// Fallback motorsport hero pool (used until site-config loads).
const HERO_POOL = [
  // Motocross — rider mid-air wheelie in desert (Joey Chacon)
  "https://images.unsplash.com/photo-1765568562583-4252b2d854b0?auto=format&fit=crop&w=1400&q=80",
  // Motocross — mid-air rider (Gijs Coolen)
  "https://images.unsplash.com/photo-1751356424654-4359798de3ef?auto=format&fit=crop&w=1400&q=80",
  // Motocross — dirt bike rider speeding down track (Gijs Coolen)
  "https://images.unsplash.com/photo-1751356424667-4b009f53bb4b?auto=format&fit=crop&w=1400&q=80",
  // Motocross — dirt bike rider jumping (Tyler Delgado)
  "https://images.unsplash.com/photo-1701602059852-4894cf75211e?auto=format&fit=crop&w=1400&q=80",
  // Motocross — yellow/black suit rider on track (Luigi Ricchezza)
  "https://images.unsplash.com/photo-1579724626828-f042c79aa188?auto=format&fit=crop&w=1400&q=80",
  // Off-road / enduro bike leaning through a corner
  "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?auto=format&fit=crop&w=1400&q=80",
  // Race car on a circuit
  "https://images.unsplash.com/photo-1541348263662-e068662d82af?auto=format&fit=crop&w=1400&q=80",
  // Road bike leaning in turn
  "https://images.unsplash.com/photo-1580310614729-ccd69652491d?auto=format&fit=crop&w=1400&q=80",
  // Muscle / sports car headlights
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1400&q=80",
];

// One "cell" of the hero collage — cycles through the pool with a
// crossfade. `slot` sets the initial offset so panels never sync.
function RotatingCell({ slot, pool }) {
  const [idx, setIdx] = useState(slot % pool.length);

  useEffect(() => {
    setIdx(slot % pool.length);
    // Stagger each slot's first switch so panels flip at different times.
    const stagger = setTimeout(() => {
      setIdx((i) => (i + 3) % pool.length);
    }, 4500 + slot * 1500);

    const id = setInterval(() => {
      setIdx((i) => (i + 3) % pool.length);
    }, 6500);

    return () => { clearTimeout(stagger); clearInterval(id); };
  }, [slot, pool]);

  return (
    <div className="relative overflow-hidden bg-black">
      {pool.map((src, i) => (
        <img
          key={`${i}-${src}`}
          src={src}
          alt=""
          loading={i === slot ? "eager" : "lazy"}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: i === idx ? 1 : 0,
            transform: i === idx ? "scale(1.06)" : "scale(1)",
            transition: "opacity 1400ms ease-in-out, transform 8000ms ease-out",
          }}
        />
      ))}
    </div>
  );
}

function BrandPanel({ site }) {
  return (
    <div className="w-full h-full bg-[var(--pm-orange)] flex flex-col items-center justify-center p-8 text-center relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{ backgroundImage: "repeating-linear-gradient(45deg, #000 0 12px, transparent 12px 28px)" }}
      />
      {site.has_nav_logo ? (
        <img
          src={brandAssetUrl("/api/public/branding/nav_logo", site.branding_version)}
          alt={site.siteName}
          className="relative max-h-[50%] max-w-[80%] w-auto object-contain"
          style={{ filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.35))" }}
        />
      ) : site.preset_key === "photomoto" ? (
        <img
          src="/brand/photomoto-icon.png"
          alt={site.siteName}
          className="relative max-h-[50%] w-auto object-contain"
          style={{ filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.35))" }}
        />
      ) : (
        <p className="relative font-display text-black text-5xl md:text-6xl uppercase leading-none">
          {site.site_name_primary}<br />{site.site_name_accent}
        </p>
      )}
      <p className="relative font-display text-black text-3xl md:text-4xl lg:text-5xl uppercase leading-[0.95] mt-4">
        Every event.<br />Every photo.
      </p>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

// Split a hero line so its final word gets the accent colour.
function AccentLine({ text }) {
  const words = (text || "").replace(/\.$/, "").split(" ");
  const last = words.pop();
  return (
    <>
      {words.join(" ")} <span className="pm-orange">{last}</span>.
    </>
  );
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [heroQuery, setHeroQuery] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const site = useSiteConfig();

  const heroPool = useMemo(() => {
    const imgs = (site.hero_images || []).map((s) => brandAssetUrl(s, site.branding_version));
    return imgs.length ? imgs : HERO_POOL;
  }, [site.hero_images, site.branding_version]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[92vh] w-full overflow-hidden" data-testid="hero-section">
        {/* 4-panel motorsport collage — 3 rotating photo cells + 1 brand panel */}
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-[2px] bg-[var(--pm-orange)]">
          <RotatingCell slot={0} pool={heroPool} />
          <RotatingCell slot={1} pool={heroPool} />
          <RotatingCell slot={2} pool={heroPool} />
          <BrandPanel site={site} />
        </div>
        {/* Lighter gradients so images stay vivid but text is legible */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-transparent to-black/30" />
        <div className="absolute inset-x-0 bottom-0 tire-track opacity-80" />

        <div className="relative h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24">
          <div className="pm-slash mb-6 text-sm md:text-base fade-up">
            <span>{site.categories_strip}</span>
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl uppercase leading-[0.9] tracking-tight max-w-4xl fade-up" style={{ animationDelay: "120ms" }}>
            <AccentLine text={site.hero_line1} /><br />
            <AccentLine text={site.hero_line2} />
          </h1>
          <p className="text-base md:text-xl text-white/80 mt-8 max-w-2xl font-condensed fade-up" style={{ animationDelay: "260ms" }}>
            {site.tagline}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 fade-up" style={{ animationDelay: "400ms" }}>
            <a href="#events" className="pm-btn" data-testid="hero-cta-explore">
              Browse events
            </a>
            <Link to="/photographers" className="pm-btn-outline" data-testid="hero-cta-photographers">
              For photographers
            </Link>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              navigate(`/events?q=${encodeURIComponent(heroQuery.trim())}`);
            }}
            className="mt-8 flex max-w-xl fade-up"
            style={{ animationDelay: "520ms" }}
            data-testid="hero-search-form"
          >
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              <input
                type="search"
                value={heroQuery}
                onChange={(e) => setHeroQuery(e.target.value)}
                placeholder={`Find your ${site.event_noun} — track name…`}
                className="w-full bg-black/60 backdrop-blur border border-white/25 pl-11 pr-4 py-4 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--pm-orange)] transition-colors"
                data-testid="hero-search-input"
              />
            </div>
            <button
              type="submit"
              className="bg-[var(--pm-orange)] text-black px-6 text-xs uppercase tracking-[0.25em] font-bold hover:opacity-90 transition"
              data-testid="hero-search-submit"
            >
              Search
            </button>
          </form>
        </div>
      </section>

      {/* Events */}
      <section id="events" className="max-w-[1600px] mx-auto px-6 md:px-12 mt-20" data-testid="events-section">
        <div className="flex items-end justify-between mb-10 pb-6 border-b-2 border-[var(--pm-orange-40)]">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">{site.calendar_label}</p>
            <h2 className="font-display text-4xl md:text-6xl uppercase mt-2">Events</h2>
          </div>
          <div className="flex items-center gap-6">
            <p className="text-sm text-white/50 hidden md:block font-condensed">
              {events.length} {events.length === 1 ? "round" : "rounds"} listed
            </p>
            <Link
              to="/events"
              className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold hover:text-white transition-colors border-b border-[var(--pm-orange)] pb-1"
              data-testid="home-view-all-events"
            >
              View &amp; filter all →
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="text-center py-24 text-white/40 text-sm uppercase tracking-[0.3em]">Loading…</p>
        ) : events.length === 0 ? (
          <div className="text-center py-24" data-testid="empty-events">
            <p className="pm-orange text-sm uppercase tracking-[0.3em] mb-4 font-semibold">No events listed yet</p>
            <p className="text-white/50 text-sm">Check back after the next {site.event_noun}.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev, idx) => (
              <Link
                key={ev.id}
                to={`/event/${ev.id}`}
                className="pm-card block aspect-[4/3] fade-up"
                style={{ animationDelay: `${idx * 60}ms` }}
                data-testid={`event-card-${ev.id}`}
              >
                {ev.cover_image_id ? (
                  <img src={thumbUrl(ev.cover_image_id)} alt={ev.track} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#111]" />
                )}
                <div className="absolute top-4 left-4 pm-slash text-xs">
                  <span>{fmtDate(ev.date)}</span>
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent pt-16 pb-5 px-5 pointer-events-none">
                  <h3 className="font-display text-2xl md:text-3xl uppercase text-white leading-tight">{ev.track}</h3>
                  {ev.name && <p className="text-sm text-white/70 mt-1 font-condensed">{ev.name}</p>}
                  <p className="text-xs uppercase tracking-[0.2em] pm-orange mt-2 font-semibold">
                    {ev.group_count} {ev.group_count === 1 ? "class" : "classes"} · {ev.photo_count} photos
                  </p>
                </div>
                <div className="reveal">
                  <div />
                  <span className="text-xs uppercase tracking-[0.2em] text-black bg-[var(--pm-orange)] px-3 py-2 font-bold">Open</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
